// utils/surcharge.js

/**
 * When list/compare-at is missing or not above the sale price but `discountCode`
 * is a percent-off value, derive list price as sale ÷ (1 − pct/100) so strikethrough
 * pricing stays consistent (e.g. trimmed API payloads or products skipped in surcharge).
 */
export function compareAtWithDiscountFallback(sale, compareAt, discountPct) {
  const base = Number(sale);
  let cmp = Number(compareAt);
  const pct = Number(discountPct);
  if (!Number.isFinite(base) || base <= 0) return cmp;
  if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) return cmp;
  if (Number.isFinite(cmp) && cmp > base + 0.009) return cmp;
  return Math.round((base / (1 - pct / 100)) * 100) / 100;
}

/**
 * @param {any[]} products
 * @param {{ ignoreStoredUserInfo?: boolean }} [options] When true, skips localStorage `userInfo` (matches SSR; use until client prefs are hydrated).
 */
function resolveCountryName(country) {
  if (typeof country === "string") return country.trim();
  if (country?.name) return String(country.name).trim();
  return "";
}

export const calculateSurcharge = (products, options = {}) => {
    let userInfo = {};
    const skipStorage =
      options.ignoreStoredUserInfo === true || typeof window === "undefined";
    if (typeof window !== "undefined" && !skipStorage) {
      try {
        userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}") || {};
      } catch {
        userInfo = {};
      }
    }
    if (!Array.isArray(products)) return products;
  
    const userCountry = resolveCountryName(userInfo?.country);
    const userSurcharge = userInfo?.surCharge || {};
    const conversionRate = parseFloat(userInfo?.currencyRate) || 1;
  
    return products.map((data) => {
      if (!data?.seller) return data;
  
      const { seller } = data;
      const sellerCountry = resolveCountryName(seller?.country);
      const shippingConfig = seller?.shippingConfigId || {};
      const isDomestic = sellerCountry === userCountry;
      const configApply = isDomestic ? shippingConfig?.domestic : shippingConfig?.international;
  
      const isShippingEnabled = configApply?.afoma_shipping;
      const isFreeShipping = configApply?.flat_rate && configApply?.flat_rate_options?.free_shipping;
  
      let surchargeValue = 0;
      if (data.productType !== "Downloadable" && isShippingEnabled && !isDomestic && !isFreeShipping) {
        surchargeValue = userSurcharge[`${sellerCountry}-${userCountry}`] || 0;
      }
  
      let handlingFee = 0;
      if (configApply?.flat_rate && configApply?.flat_rate_options?.free_shipping && data.productType !== "Downloadable") {
        const flatRateOptions = configApply?.flat_rate_options;
        handlingFee = flatRateOptions?.is_flat_rate
          ? flatRateOptions?.flat_rate_rate
          : data.weight <= 1
          ? flatRateOptions?.flat_rate_0_1
          : data.weight <= 5
          ? flatRateOptions?.flat_rate_1_5
          : flatRateOptions?.flat_rate_5_A;
      }

      if(data?.freeDelivery && sellerCountry == userCountry){
        handlingFee = parseFloat(data?.handlingFee) || 0
      }
  
      const basePrice = parseFloat(data.price) || 0;
      const discountFactor = data.discountCode ? 1 - data.discountCode / 100 : 1;
      const discountedAmount = basePrice * discountFactor;
      const amountWithSurcharge = parseFloat((discountedAmount + surchargeValue + handlingFee).toFixed(2));
      
      if(data.productType === "Standard"){
        // SET FINAL AND TOTAL PRICES
        data.finalPrice = parseFloat((discountedAmount + handlingFee).toFixed(2))
        data.surTotalAmount = parseFloat((amountWithSurcharge * conversionRate));
        data.surTotalAmountBDis = data.discountCode
          ? parseFloat(((amountWithSurcharge / discountFactor) * conversionRate))
          : data.surTotalAmount;
      }

      if(data.productType === "Downloadable"){
        data.surTotalAmount = parseFloat((discountedAmount * conversionRate));
        data.surTotalAmountBDis = data.discountCode
          ? parseFloat(((discountedAmount / discountFactor) * conversionRate))
          : data.surTotalAmount;
      }

      if (data.productType === "Service") {
        const servicePrice =
          parseFloat(data.finalPrice) || parseFloat((basePrice * discountFactor).toFixed(2));
        data.finalPrice = servicePrice;
        data.surTotalAmount = parseFloat((servicePrice * conversionRate).toFixed(2));
        data.surTotalAmountBDis = data.discountCode
          ? parseFloat((basePrice * conversionRate).toFixed(2))
          : data.surTotalAmount;
      }
  
      if (data.productType === "Customizable" && Array.isArray(data.variations)) {
        data.variations.forEach((variation) => {
          // SET FINAL AND TOTAL PRICES
          const variationPrice = parseFloat(variation.price) || 0;
          const varDiscountedAmount = variationPrice * discountFactor;
          const varAmountWithSurcharge = parseFloat((varDiscountedAmount + surchargeValue + handlingFee).toFixed(2));
  
          variation.surTotalAmount = parseFloat((varAmountWithSurcharge * conversionRate));
          variation.surTotalAmountBDis = data.discountCode
            ? parseFloat(((varAmountWithSurcharge / discountFactor) * conversionRate))
            : variation.surTotalAmount;
            // if(data.sku = "SETOFHANB9ABE7CC" && variation?.Fragrance == "Lavender & Honey"){
            //   console.log(parseFloat(varAmountWithSurcharge.toFixed(2)) * conversionRate)
            //   console.log(variation.surTotalAmount)
            //   console.log(varAmountWithSurcharge)
            //   console.log(parseFloat(varDiscountedAmount + handlingFee))
            // }
          variation.finalPrice = parseFloat((varDiscountedAmount + handlingFee).toFixed(2))
        });
      }
  
      return data;
    });
  };

  export const calculateItemPrice = (data, order, converted = true, seller = false) => {
    if(data?.productData?.productType !== "Customizable"){
      const basePrice = data.productData.finalPrice ?? data.basePrice;
        const priceWithShipping =
          data?.shippingOptions?.length && !seller
          ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
            : basePrice;
  
        return order.conversionRate && converted
          ? order.conversionRate * priceWithShipping
          : priceWithShipping;
    }else{
      const selectedVariation = data?.productData?.variations?.find(
        (variation) => variation?.[data.selectedVariations?.[0]?.attributeName] === data.selectedVariations?.[0]?.attributeValue
      );
      const basePrice = selectedVariation?.finalPrice ?? data.basePrice;

      const priceWithShipping =
        data?.shippingOptions?.length && !seller
        ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
          : basePrice;

      return order.conversionRate && converted
        ? order.conversionRate * priceWithShipping
        : priceWithShipping;
    }

  }

  export const calculateItemTotalPrice = (data, order, converted = true, seller = false) => {
    if(data.productData.productType != "Customizable"){
      const discountFactor = data.productData.discountCode ? 1 - data.productData.discountCode / 100 : 1;
      const basePrice = data.productData.finalPrice ?? data.totalAmount; 
  
      const priceWithShipping =
        data?.shippingOptions?.length && !seller
        ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
          : basePrice;
      let coupon = data.productData.couponCode && data.productData.couponDiscount ? data.productData.couponDiscount : 0
      const amount = (Number(priceWithShipping) + coupon ) / discountFactor

      return order.conversionRate && converted
        ? order.conversionRate * amount
        :  amount;
    }else{
      const selectedVariation = data?.productData?.variations?.find(
        (variation) => variation?.[data.selectedVariations?.[0]?.attributeName] === data.selectedVariations?.[0]?.attributeValue
      );
      const discountFactor = data.productData.discountCode ? 1 - data.productData.discountCode / 100 : 1;
      const basePrice = selectedVariation?.finalPrice ?? data.totalAmount; 

      const priceWithShipping =
        data?.shippingOptions?.length && !seller
          ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
          : basePrice;

      let coupon = data.productData.couponCode && data.productData.couponDiscount ? data.productData.couponDiscount : 0
      const amount = (Number(priceWithShipping) + coupon ) / discountFactor

      return order.conversionRate && converted
        ? order.conversionRate * amount
        :  amount;
    }
}

export const calculateItemsTotalPrice = (order, converted = true, seller = false) => {
  let total = 0
  for(let data of order.cart){
    if(data?.productData?.productType !== "Customizable"){
      const basePrice = data.productData.finalPrice ?? data.basePrice;
        const priceWithShipping =
          data?.shippingOptions?.length && !seller
          ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
            : basePrice;
  
        total += Number(
          order.conversionRate && converted
            ? order.conversionRate * priceWithShipping
            : priceWithShipping
        );
    }else{
      const selectedVariation = data?.productData?.variations?.find(
        (variation) => variation?.[data.selectedVariations?.[0]?.attributeName] === data.selectedVariations?.[0]?.attributeValue
      );
      const basePrice = selectedVariation?.finalPrice ?? data.basePrice;

      const priceWithShipping =
        data?.shippingOptions?.length && !seller
        ? (Number(basePrice) + (data?.shippingOptions[0]?.deductedAmount ? Number(data?.shippingOptions[0]?.deductedAmount) : 0))
          : basePrice;

      total += Number(
        order.conversionRate && converted
          ? order.conversionRate * priceWithShipping
          : priceWithShipping
      );
    }
  }

  return total ? Number(total) : ""
}

/** Sum of per-line totals — matches the order table "Total" column (avoids string-concatenated `order.subTotal`). */
export const calculateOrderItemsSubTotal = (order, converted = true, seller = false) => {
  if (!order?.cart?.length) return 0;
  return order.cart.reduce(
    (sum, item) =>
      sum + (Number(calculateItemTotalPrice(item, order, converted, seller)) || 0),
    0
  );
};

export const calculateOrderShippingTotal = (order) => {
  if (!order?.cart?.length) return 0;
  const seen = new Set();
  return order.cart.reduce((total, item) => {
    const sellerId =
      item.productData?.seller?._id || item.productData?.seller?.id;
    if (!sellerId || seen.has(sellerId)) return total;
    seen.add(sellerId);
    return total + (Number(item.shippingRate) || 0);
  }, 0);
};

export const calculateOrderServiceFees = (order, converted = true) => {
  const fees = Number(order?.serviceFees) || 0;
  if (!fees) return 0;
  return converted && order?.conversionRate
    ? order.conversionRate * fees
    : fees;
};

export const calculateOrderGrandTotal = (order, converted = true, seller = false) => {
  return (
    calculateOrderItemsSubTotal(order, converted, seller) +
    calculateOrderServiceFees(order, converted) +
    calculateOrderShippingTotal(order)
  );
};

export const calculateShippingRate = (order) => {
  const sellers = new Set();
  let total = 0;

  order.cart.forEach(({ productData, shippingRate }) => {
    const sellerId = productData?.seller?._id || productData?.seller;
    if (sellerId && !sellers.has(sellerId)) {
      sellers.add(sellerId);
      total += shippingRate > 0 ? shippingRate : 0;
    }
  });
  return order.conversionRate > 0 ? total / order.conversionRate : total;
};

/** Buyer country string for surcharge keys (matches calculateSurcharge / cart). */
export function getBuyerCountryString(userInfoStored) {
  const c = userInfoStored?.country;
  if (!c) return "";
  if (typeof c === "string") return c.trim();
  if (typeof c === "object" && c.name) return String(c.name).trim();
  return String(c).trim();
}

/**
 * International AFOMA shipping surcharge (CAD) for one seller group — mirrors * `getShippingRate` in pages/_app.js (first item drives return value).
 */
export function getInternationalDeductedCadForSellerGroup(
  products,
  buyerCountryOverride
) {
  if (!Array.isArray(products) || !products.length) return 0;
  const userInfo =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("userInfo") || "{}")
      : {};
  const userSurcharge = userInfo?.surCharge || {};
  const fromStorage =
    typeof userInfo?.country === "string"
      ? userInfo.country.trim()
      : (userInfo?.country?.name && String(userInfo.country.name).trim()) || "";
  const buyerCountry =
    (buyerCountryOverride && String(buyerCountryOverride).trim()) || fromStorage;

  let surcharge = 0;
  for (const item of products) {
    if (
      item.productData?.productType !== "Downloadable" &&
      item.productData?.seller?.shippingConfigId?.international?.afoma_shipping &&
      item.productData?.seller?.country?.trim() !== buyerCountry
    ) {
      surcharge =
        userSurcharge[
          `${item.productData?.seller?.country?.trim()}-${buyerCountry}`
        ] || 0;
    }
    const qty = parseFloat(item.orderQuantiy) || 0;
    return surcharge * ((qty - 1) + products.length);
  }
  return 0;
}

/** Match customizable line to a variation row (cart / AppShell). */
export function findCartVariation(productData, selectedVariations) {
  if (!productData?.variations?.length || !selectedVariations?.length) {
    return null;
  }
  return productData.variations.find((variation) =>
    selectedVariations.every(
      (selected) =>
        variation[selected.attributeName] === selected.attributeValue
    )
  );
}

/**
 * Unit price stored on each cart line (CAD). Matches `calculateSurcharge` `finalPrice`:
 * product discount + flat-rate handling baked in. Intl AFOMA surcharge stays out of
 * `basePrice` and is added in `saveCart` via `deductedAm` (avoids double-counting).
 */
export function getCartUnitPriceCad(productData, selectedVariations) {
  if (!productData) return 0;

  if (
    productData.productType === "Customizable" &&
    selectedVariations?.length
  ) {
    const variation = findCartVariation(productData, selectedVariations);
    if (variation?.finalPrice != null && Number.isFinite(Number(variation.finalPrice))) {
      return parseFloat(Number(variation.finalPrice).toFixed(2));
    }
    if (variation?.price != null && Number.isFinite(Number(variation.price))) {
      return parseFloat(Number(variation.price).toFixed(2));
    }
  }

  if (
    productData.finalPrice != null &&
    Number.isFinite(Number(productData.finalPrice))
  ) {
    return parseFloat(Number(productData.finalPrice).toFixed(2));
  }

  return parseFloat(productData.price) || 0;
}

/** Re-run surcharge + flat-rate handling on a cart line's product snapshot. */
export function refreshCartLineProductData(productData, userInfo) {
  if (!productData) return productData;
  const priced = calculateSurcharge([productData], {
    ignoreStoredUserInfo: !userInfo?.country,
  })?.[0];
  return priced ?? productData;
}

/**
 * Cart row total in the user's display currency (e.g. PKR). Uses each line's
 * `totalAmount` (CAD, after coupon) plus a proportional share of the seller's
 * international surcharge — same composition as `subTotal` in saveCart, so row
 * totals align with Summary "Item(s) total" and with listing cards that include surcharge.
 */
export function getCartLineDisplayAmountUserCurrency(
  lineItem,
  sellerGroupLines,
  userInfoStored
) {
  const rate = parseFloat(userInfoStored?.currencyRate) || 1;
  const buyer = getBuyerCountryString(userInfoStored);
  const deductedCad = getInternationalDeductedCadForSellerGroup(
    sellerGroupLines,
    buyer
  );
  const groupSub = sellerGroupLines.reduce(
    (s, it) => s + parseFloat(it.totalAmount || 0),
    0
  );
  const lineCad = parseFloat(lineItem.totalAmount || 0);
  const shareCad =
    groupSub > 0 ? (lineCad / groupSub) * deductedCad : 0;
  return (lineCad + shareCad) * rate;
}
